import React, {useCallback, useMemo, useState} from 'react';
import {useLocation} from "react-router-dom";
import ReactModal from "react-modal";
import {CellProps, Column} from "react-table";
import {Menu} from "../../../../types/admin/menu";
import {menuApi} from "../../../../apis/admin/menu/menuApi";
import {getMenuTypeText, MenuType} from "../../../../enum/menu/MenuType";
import {ListTableWithSearchBox, SearchParams} from "../../common/table/ListTableWithSearchBox";
import useAppRouter from "../../../../hooks/useAppRouter";
import {formatOmittedDateTime} from "../../../../libs/utils/formatDate";
import {logError} from "../../../../libs/utils/logError";

interface DataType extends Menu {
  operation?: string
}

interface SelectMenuModalProps {
  selectedMenuIdMap: Map<number, boolean>;
  selectedMenuMaterialIdMap: Map<number, boolean>;
  selectedVhPresentationIdMap: Map<string, boolean>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect: (menu: Menu) => Promise<void>;
  onSelected: () => Promise<void>;
}

export const SelectMenuModal = (props: SelectMenuModalProps) => {
  const {
    selectedMenuIdMap,
    selectedMenuMaterialIdMap,
    selectedVhPresentationIdMap,
    setShowModal,
    onSelect,
    onSelected
  } = props;
  const [menuList, setMenuList] = useState<DataType[]>([]);
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const [totalCount, setTotalCount] = React.useState<number>(10);
  const { goto } = useAppRouter();
  const location = useLocation();

  const sortFunc = async (params: SearchParams) => {
    setAbortController(new AbortController());
    try
    {
      const offset = (params.currentPage - 1) * params.size;
      const response = await menuApi.index(params.keyword, params.size, offset, params.sort, { abortSignal: abortController?.signal });
      setAbortController(undefined);
      setMenuList(response.menuList);
      setTotalCount(response.totalHits);
    } catch(e) {
      await logError(
        location.pathname,
        'メニュー一覧の取得に失敗しました。',
        e
      );

      goto('internalServerError');
    }
  };

  const initialSort = [
    'createdAt:desc'
  ];

  const isSelectDisabled = useCallback((menu: DataType): boolean => {
    return selectedMenuIdMap.has(menu.id)
      || ((menu.menuTypeId === MenuType.MATERIAL) && (menu.menuMaterialId !== null) && selectedMenuMaterialIdMap.has(menu.menuMaterialId))
      || ((menu.menuTypeId === MenuType.VH_PRESENTATION) && (menu.vhPresentationId !== null) && selectedVhPresentationIdMap.has(menu.vhPresentationId));
  }, [selectedMenuIdMap, selectedMenuMaterialIdMap, selectedVhPresentationIdMap]);

  const MenuTypeCell = ({ value }: CellProps<Menu>) => {
    return (
      <>
        <div>{ getMenuTypeText(value) }</div>
      </>
    );
  };

  const DateTimeCell = useCallback(({ value }: CellProps<DataType>) => (
    <div>{ formatOmittedDateTime(value) }</div>
  ), []);

  const OperationCell = useCallback(({ row }: CellProps<DataType>) => {
    const handleSelectClick = async (menu: Menu): Promise<void> => {
      await onSelect(menu);
      await onSelected();
    };

    return (
      <>
        <button
          className={`btn-primary ${ isSelectDisabled(row.original) ? 'btn-primary-disabled' : ''}`}
          type='button'
          onClick={() => {
            handleSelectClick(row.original);
          }}
          disabled={isSelectDisabled(row.original)}
        >
          選択
        </button>
      </>
    );
  }, [onSelect, onSelected, isSelectDisabled]);

  const columns: Column<DataType>[] = useMemo(
    () => [
      {
        Header: '名称',
        accessor: 'name',
        className: 'select-none',
      },
      {
        Header: 'メニュータイプ',
        accessor: 'menuTypeId',
        Cell: MenuTypeCell,
        disableSortBy: true,
      },
      {
        Header: '作成日時',
        accessor: 'createdAt',
        Cell: DateTimeCell,
      },
      {
        Header: '操作',
        accessor: 'operation',
        Cell: OperationCell,
        disableSortBy: true,
      },
    ],
    [OperationCell, DateTimeCell]
  );

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => setShowModal(false)}
        className="select-modal"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">メニュー選択</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => setShowModal(false)}
            >
              ×
            </button>
          </div>
          <div className="form-modal-content">
              <div className="p-4">
                <ListTableWithSearchBox
                  {...{
                    tableData: menuList,
                    columns,
                    initialSort,
                    totalCount,
                    sortFunc,
                  }}
                />
              </div>
          </div>
          <div className="form-modal-footer-container">
            <div className="form-modal-footer">
              <button
                className="btn-sub"
                type="button"
                onClick={() => setShowModal(false)}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      </ReactModal>

    </>
  );
};
